import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, Users, LogIn } from "lucide-react";

export default function CohostAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/cohost/${token}`)
      .then(({ data }) => { setInfo(data); setEmail(data.email || ""); })
      .catch(() => setNotFound(true));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/cohost/accept", { token });
      toast.success("You're now a co-host!");
      navigate(`/e/${data.id}/dashboard`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setAccepting(false); }
  };

  const doAuth = async () => {
    if (!email || !password || (mode === "register" && !name)) { toast.error("Please fill in all fields."); return; }
    setBusy(true);
    try {
      if (mode === "register") await register(name, email, password);
      else await login(email, password);
      await accept();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (notFound) return <Centered><p className="font-serif text-3xl text-stone-500">This co-host invitation is no longer valid.</p></Centered>;
  if (!info) return <Centered><p className="font-serif text-3xl text-stone-300">Loading…</p></Centered>;

  return (
    <Centered>
      <div className="bg-white border border-[#E8E6E1] rounded-xl p-6 sm:p-8 w-full max-w-md fade-up" data-testid="cohost-accept-page">
        <div className="w-12 h-12 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86] mx-auto mb-4"><Users size={20} /></div>
        <h1 className="font-serif text-3xl text-center">Co-host {info.event_title}</h1>
        <p className="text-sm text-stone-500 text-center mt-2">{info.inviter} invited you to co-host this event. Accept to get full access to help plan it.</p>

        {user ? (
          <button onClick={accept} disabled={accepting} data-testid="cohost-accept-btn"
            className="mt-6 w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40">
            {accepting ? "Please wait…" : "Accept & co-host"}
          </button>
        ) : (
          <>
            <div className="space-y-3 mt-6">
              {mode === "register" && <Field label="Your name" value={name} onChange={setName} testid="cohost-name" />}
              <Field label="Email" type="email" value={email} onChange={setEmail} testid="cohost-auth-email" />
              <Field label="Password" type="password" value={password} onChange={setPassword} testid="cohost-auth-password" />
            </div>
            <button onClick={doAuth} disabled={busy} data-testid="cohost-auth-submit"
              className="mt-6 w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              <LogIn size={15} /> {busy ? "Please wait…" : (mode === "register" ? "Create account & accept" : "Sign in & accept")}
            </button>
            <p className="text-center text-sm text-stone-500 mt-4">
              {mode === "register" ? "Already have an account?" : "New to Evermore?"}{" "}
              <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="cohost-auth-toggle" className="text-[#8C9A86] hover:underline">
                {mode === "register" ? "Sign in" : "Create one"}
              </button>
            </p>
          </>
        )}
      </div>
    </Centered>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
function Centered({ children }) {
  return <div className="min-h-screen grid place-items-center px-4" style={{ background: "#FDFBF7" }}>{children}</div>;
}
